import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { Button } from '@/components/ui/button';
import { Move, Maximize2, Minimize2, RotateCcw, Save, X, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface LogoEditorProps {
  children: React.ReactNode;
  logoId: string;
  className?: string;
  enableHeightControl?: boolean;
  isEditing?: boolean; // Controls whether edit UI is shown
}

interface LogoSettings {
  scale: number;
  x: number;
  y: number;
  height?: number;
}

interface LogoEditorContextType {
  registerLogo: (id: string, settings: LogoSettings, setSettings: (settings: LogoSettings) => void) => void;
  unregisterLogo: (id: string) => void;
  logosRef: React.MutableRefObject<Map<string, { settings: LogoSettings; setSettings: (s: LogoSettings) => void }>>;
}

const LogoEditorContext = createContext<LogoEditorContextType | null>(null);

export const LogoEditor: React.FC<LogoEditorProps> = ({ children, logoId, className, enableHeightControl = false, isEditing = true }) => {
  const context = useContext(LogoEditorContext);
  
  // Initialize settings from localStorage IMMEDIATELY (synchronously)
  const [settings, setSettings] = useState<LogoSettings>(() => {
    // Try to load from localStorage first
    const saved = localStorage.getItem(`logo-editor-${logoId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed;
      } catch (e) {
        console.error('Failed to parse logo settings:', e);
      }
    }
    // Default values if nothing saved
    return { 
      scale: 1, 
      x: 0, 
      y: 0, 
      height: enableHeightControl ? 110 : undefined 
    };
  });
  
  const [isLoading, setIsLoading] = useState(true);

  // Load settings from database on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Always try localStorage first
        const saved = localStorage.getItem(`logo-editor-${logoId}`);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setSettings(parsed);
            initialSettings.current = parsed;
          } catch (e) {
            console.error('Failed to parse logo settings:', e);
          }
        }

        // If authenticated, also sync with database
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('logo_settings')
            .eq('user_id', user.id)
            .single();

          if (profile?.logo_settings?.[logoId]) {
            const loaded = profile.logo_settings[logoId];
            setSettings(loaded);
            initialSettings.current = loaded;
            // Update localStorage to match database
            localStorage.setItem(`logo-editor-${logoId}`, JSON.stringify(loaded));
          }
        }
      } catch (error) {
        console.error('Failed to load logo settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [logoId, enableHeightControl]);

  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isCornerResizing, setIsCornerResizing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, startX: 0, startY: 0 });
  const pinchStart = useRef({ distance: 0, scale: 1 });
  const resizeStart = useRef({ clientY: 0, startScale: 1 });
  const initialSettings = useRef<LogoSettings>(settings);

  // Track changes
  useEffect(() => {
    if (isLoading) return;
    const changed = 
      settings.scale !== initialSettings.current.scale ||
      settings.x !== initialSettings.current.x ||
      settings.y !== initialSettings.current.y ||
      settings.height !== initialSettings.current.height;
    setHasChanges(changed);
  }, [settings, isLoading]);

  // Manual save function
  const handleSave = async () => {
    try {
      // Always save to localStorage for immediate access
      localStorage.setItem(`logo-editor-${logoId}`, JSON.stringify(settings));

      // Try to save to database if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('logo_settings')
          .eq('user_id', user.id)
          .single();

        // Handle the JSONB type from Supabase
        let currentSettings: Record<string, LogoSettings> = {};
        if (profile?.logo_settings && typeof profile.logo_settings === 'object' && !Array.isArray(profile.logo_settings)) {
          currentSettings = profile.logo_settings as unknown as Record<string, LogoSettings>;
        }

        const updatedSettings = {
          ...currentSettings,
          [logoId]: settings
        };

        await supabase
          .from('profiles')
          .update({ logo_settings: updatedSettings as any })
          .eq('user_id', user.id);
      }

      initialSettings.current = settings;
      setHasChanges(false);
      toast.success('Logo size saved!');
    } catch (error) {
      console.error('Failed to save logo settings:', error);
      toast.error('Failed to save');
    }
  };

  // Save settings to both localStorage and database on change (removed - now manual only)
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load
    // Auto-save removed - now using manual save button
  }, [settings, logoId, isLoading]);

  useEffect(() => {
    if (context) {
      context.registerLogo(logoId, settings, setSettings);
      return () => context.unregisterLogo(logoId);
    }
  }, [context, logoId, settings, setSettings]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      startX: settings.x,
      startY: settings.y,
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;
    
    setSettings(prev => ({
      ...prev,
      x: dragStart.current.startX + deltaX,
      y: dragStart.current.startY + deltaY,
    }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.touches.length === 1) {
      setIsDragging(true);
      dragStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        startX: settings.x,
        startY: settings.y,
      };
    } else if (e.touches.length === 2) {
      setIsResizing(true);
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      pinchStart.current = { distance, scale: settings.scale };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging) {
      const deltaX = e.touches[0].clientX - dragStart.current.x;
      const deltaY = e.touches[0].clientY - dragStart.current.y;
      
      setSettings(prev => ({
        ...prev,
        x: dragStart.current.startX + deltaX,
        y: dragStart.current.startY + deltaY,
      }));
    } else if (e.touches.length === 2 && isResizing) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scaleChange = distance / pinchStart.current.distance;
      const newScale = Math.max(0.3, Math.min(5, pinchStart.current.scale * scaleChange));
      
      setSettings(prev => ({
        ...prev,
        scale: newScale,
      }));
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  // Corner resize handlers
  const handleCornerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCornerResizing(true);
    resizeStart.current = {
      clientY: e.clientY,
      startScale: settings.scale,
    };
  };

  const handleCornerMouseMove = (e: MouseEvent) => {
    if (!isCornerResizing) return;
    e.preventDefault();
    
    const deltaY = resizeStart.current.clientY - e.clientY; // Inverted: drag down = smaller, drag up = bigger
    const scaleChange = deltaY * 0.005;
    const newScale = Math.max(0.3, Math.min(5, resizeStart.current.startScale + scaleChange));
    
    setSettings(prev => ({
      ...prev,
      scale: newScale,
    }));
  };

  const handleCornerMouseUp = () => {
    setIsCornerResizing(false);
  };

  const handleCornerTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.touches.length === 1) {
      setIsCornerResizing(true);
      resizeStart.current = {
        clientY: e.touches[0].clientY,
        startScale: settings.scale,
      };
    }
  };

  const handleCornerTouchMove = (e: React.TouchEvent) => {
    if (!isCornerResizing || e.touches.length !== 1) return;
    e.preventDefault();
    
    const deltaY = resizeStart.current.clientY - e.touches[0].clientY;
    const scaleChange = deltaY * 0.005;
    const newScale = Math.max(0.3, Math.min(5, resizeStart.current.startScale + scaleChange));
    
    setSettings(prev => ({
      ...prev,
      scale: newScale,
    }));
  };

  const handleCornerTouchEnd = () => {
    setIsCornerResizing(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    
    if (enableHeightControl && e.shiftKey && settings.height !== undefined) {
      // Shift + scroll = adjust height
      const delta = e.deltaY * -0.5;
      const newHeight = Math.max(100, Math.min(500, settings.height + delta));
      
      setSettings(prev => ({
        ...prev,
        height: newHeight,
      }));
    } else {
      // Normal scroll = adjust scale
      const delta = e.deltaY * -0.002;
      const newScale = Math.max(0.3, Math.min(5, settings.scale + delta));
      
      setSettings(prev => ({
        ...prev,
        scale: newScale,
      }));
    }
  };

  useEffect(() => {
    if (context) {
      const currentEntry = context.logosRef.current.get(logoId);
      if (currentEntry) {
        context.logosRef.current.set(logoId, { settings, setSettings });
      }
    }
  }, [settings, context, logoId]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, settings]);

  useEffect(() => {
    if (isCornerResizing) {
      window.addEventListener('mousemove', handleCornerMouseMove);
      window.addEventListener('mouseup', handleCornerMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleCornerMouseMove);
        window.removeEventListener('mouseup', handleCornerMouseUp);
      };
    }
  }, [isCornerResizing, settings]);

  // Update context when settings change
  useEffect(() => {
    // Settings updated
  }, [settings, isEditing, logoId]);

  return (
    <div className="relative inline-block w-full">
      <div
        ref={elementRef}
        className={cn(
          isEditing && "cursor-move touch-none select-none",
          "transition-transform duration-100",
          className
        )}
        style={{
          transform: `translate(${settings.x}px, ${settings.y}px) scale(${settings.scale})`,
          transformOrigin: 'center',
          ...(enableHeightControl && settings.height !== undefined ? { height: `${settings.height}px` } : {}),
        }}
        onMouseDown={isEditing ? handleMouseDown : undefined}
        onTouchStart={isEditing ? handleTouchStart : undefined}
        onTouchMove={isEditing ? handleTouchMove : undefined}
        onTouchEnd={isEditing ? handleTouchEnd : undefined}
        onWheel={isEditing ? handleWheel : undefined}
      >
        {children}
      </div>
      
      {isEditing && (
        <>
          <div className="absolute top-0 left-0 w-full h-full border-2 border-dashed border-primary/30 pointer-events-none rounded-lg" />
          
          {/* Corner resize handles */}
          <div 
            className="absolute bottom-0 right-0 w-8 h-8 cursor-nwse-resize pointer-events-auto z-10 group"
            onMouseDown={handleCornerMouseDown}
            onTouchStart={handleCornerTouchStart}
            onTouchMove={handleCornerTouchMove}
            onTouchEnd={handleCornerTouchEnd}
          >
            <div className="absolute bottom-0 right-0 w-6 h-6 bg-primary/20 group-hover:bg-primary/40 border-2 border-primary rounded-tl-lg transition-colors" />
            <Maximize2 className="absolute bottom-0.5 right-0.5 h-4 w-4 text-primary" />
          </div>
          
          {/* Scale indicator */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded whitespace-nowrap">
            {Math.round(settings.scale * 100)}%
            {enableHeightControl && settings.height !== undefined && (
              <span className="ml-2">• {Math.round(settings.height)}px</span>
            )}
          </div>
          
          {/* Save button - appears when there are unsaved changes */}
          {hasChanges && (
            <Button
              size="sm"
              onClick={handleSave}
              className="absolute -bottom-14 left-1/2 -translate-x-1/2 h-8 px-3 shadow-lg animate-in fade-in slide-in-from-bottom-2"
            >
              <Save className="h-3 w-3 mr-1.5" />
              Save Size
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export const LogoEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const logosRef = useRef<Map<string, { settings: LogoSettings; setSettings: (s: LogoSettings) => void }>>(new Map());

  const registerLogo = (id: string, settings: LogoSettings, setSettings: (s: LogoSettings) => void) => {
    logosRef.current.set(id, { settings, setSettings });
  };

  const unregisterLogo = (id: string) => {
    logosRef.current.delete(id);
  };

  const contextValue: LogoEditorContextType = {
    registerLogo,
    unregisterLogo,
    logosRef
  };

  return (
    <LogoEditorContext.Provider value={contextValue}>
      {children}
    </LogoEditorContext.Provider>
  );
};

export const LogoEditorControls: React.FC<{
  isEditing: boolean;
  onToggle: () => void;
}> = ({ isEditing, onToggle }) => {
  const [isDemoUser, setIsDemoUser] = useState(false);
  // FIXED: useContext must be called before any conditional returns (React hooks rules)
  const context = useContext(LogoEditorContext);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsDemoUser(user?.email === 'support@therappi.app');
    };
    checkUser();
  }, []);

  // Hide logo editor controls for demo user
  if (isDemoUser) return null;

  const handleSaveAll = async () => {
    if (!context) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in to save logo settings');
        return;
      }

      // Settings are already saved automatically to database
      // Just show confirmation and close editor
      toast.success('Logo positions saved permanently! Your custom layout will persist across all devices.');
      onToggle(); // Close editor after save
    } catch (error) {
      console.error('Failed to save logos:', error);
      toast.error('Failed to save logo settings');
    }
  };

  const handleResetAll = async () => {
    if (!context) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in to reset logo settings');
        return;
      }

      const logos = Array.from(context.logosRef.current.entries());
      const defaultSettings = { scale: 1, x: 0, y: 0 };
      
      // Reset each logo in state
      logos.forEach(([id, { setSettings }]) => {
        setSettings(defaultSettings);
        localStorage.removeItem(`logo-editor-${id}`);
      });

      // Clear database settings
      await supabase
        .from('profiles')
        .update({ logo_settings: {} })
        .eq('user_id', user.id);

      toast.success('Logos reset to default!');
    } catch (error) {
      console.error('Failed to reset logos:', error);
      toast.error('Failed to reset logo settings');
    }
  };

  if (!isEditing) {
    return (
      <Button
        onClick={onToggle}
        variant="outline"
        size="sm"
        className="fixed top-4 right-4 z-[9999] shadow-lg h-11 px-4 text-sm font-medium"
      >
        <Edit3 className="h-4 w-4 mr-2" />
        Edit Logos
      </Button>
    );
  }

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col sm:flex-row gap-2 bg-background/95 backdrop-blur-sm border border-border rounded-lg p-3 shadow-xl max-w-md">
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground border-b sm:border-b-0 sm:border-r border-border">
        <Move className="h-4 w-4" />
        <span>Drag to move • Corner to resize</span>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={handleResetAll}
          className="h-10 px-3"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset
        </Button>
        <Button
          size="sm"
          variant="default"
          onClick={handleSaveAll}
          className="h-10 px-4"
        >
          <Save className="h-4 w-4 mr-2" />
          Save & Close
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onToggle}
          className="h-10 w-10 p-0"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};
